import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";

/**
 * Pencil components `hkOTI` (active) / `oP6PQ` (default). Full-width
 * segmented-control: the tab list spans the parent's width; each
 * trigger takes an equal share via `flex-1`. Active tab lifts with
 * `bg-card + shadow-sm` against the muted container; no one-sided
 * borders anywhere — every visible edge of the active tab reads equal
 * weight.
 *
 * Backed by `@radix-ui/react-tabs` — Arrow Left/Right cycles focus +
 * activation, Home/End jumps to first/last. We render only the trigger
 * row here; callers continue to render their own content panels by
 * switching on `value`, so existing
 * `{tab === "details" && <DetailsTab />}` patterns keep working.
 */
export function Tabs<T extends string>({
  value,
  onChange,
  items,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  items: { key: T; label: React.ReactNode; count?: number }[];
  className?: string;
}) {
  return (
    <TabsPrimitive.Root
      value={value}
      onValueChange={(v) => onChange(v as T)}
      // className flows to the Root wrapper so callers can still use
      // padding utilities like `px-5` to align the segmented control
      // inside their panel without disturbing the pill's internal
      // padding.
      className={className}
    >
      <TabsPrimitive.List
        className={cn(
          // Full-width container with each trigger flexing to share
          // the available width equally. NO inner padding — triggers
          // run edge-to-edge inside the bordered pill. `overflow-hidden`
          // clips trigger backgrounds to the container's rounded
          // corners cleanly.
          "flex w-full items-stretch rounded-md overflow-hidden",
          "border border-border bg-muted/50",
        )}
      >
        {items.map((it) => (
          <TabsPrimitive.Trigger
            key={it.key}
            value={it.key}
            className={cn(
              "flex-1 min-w-0 inline-flex items-center justify-center gap-1.5",
              "px-3 py-2 text-sm font-medium",
              "whitespace-nowrap transition-colors",
              "data-[state=active]:bg-card data-[state=active]:text-foreground",
              "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground",
              // Inset focus ring so it doesn't escape the rounded
              // container when the first/last trigger is focused.
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
            )}
          >
            <span className="truncate">{it.label}</span>
            {typeof it.count === "number" && (
              <span
                className={cn(
                  "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                  "bg-foreground/10 text-foreground/70",
                )}
              >
                {it.count}
              </span>
            )}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
    </TabsPrimitive.Root>
  );
}
